// src/components/ClientLogos.jsx
//
// The strip of client marks. One shared set across every site, managed in the
// portal.
//
// No local fallback: an empty strip is a truthful state, and a hardcoded set
// would be wrong the moment a client is added or removed. The section renders
// nothing at all until there is something to show.
//
// The scroll is a CSS animation on a duplicated track rather than a JavaScript
// carousel. It runs on the compositor, needs no timer, and pauses on hover -
// which is what makes an always-moving element tolerable rather than annoying.
//
// Nothing here is gated on useInView. An entry animation that depends on an
// observer can leave content at opacity 0 forever if the observer does not
// fire, which is a decoration being able to hide the page.

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";

const API = process.env.REACT_APP_API_URL;
const MEDIA = process.env.REACT_APP_MEDIA_URL;

// How many copies of the set to lay end to end. With a handful of logos one
// pass is narrower than the screen and the track would show a gap as it
// wraps, so the set repeats until there is comfortably more than a screenful.
function copiesFor(count) {
  if (count >= 8) return 2;
  if (count >= 4) return 3;
  return 6;
}

export default function ClientLogos() {
  const reduce = useReducedMotion();
  const [logos, setLogos] = useState(null);

  useEffect(() => {
    let alive = true;

    fetch(`${API}/v1/client-logos`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setLogos(d?.logos || []);
      })
      .catch(() => {
        if (alive) setLogos([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  if (!logos || logos.length === 0) return null;

  const copies = copiesFor(logos.length);

  // Time for one full pass. Scales with how much track there is, so the marks
  // travel at roughly the same speed whatever the count.
  const duration = Math.max(24, logos.length * copies * 4);

  return (
    <section className="overflow-hidden border-t border-line py-16 sm:py-24">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mx-auto mb-12 max-w-7xl px-5 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-subtle sm:mb-16 sm:px-8"
      >
        Trusted by
      </motion.p>

      {/* Fades at both edges, so marks enter and leave rather than being
          clipped against a hard border. */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-bg to-transparent sm:w-32" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-bg to-transparent sm:w-32" />

        <div
          className={reduce ? "flex flex-wrap items-center justify-center gap-x-16 gap-y-12 px-5" : "bk-marquee flex w-max"}
          style={reduce ? undefined : { "--marquee-duration": `${duration}s` }}
        >
          {reduce ? (
            logos.map((logo) => <Logo key={logo.id} logo={logo} />)
          ) : (
            // The track is halved by the animation, so the copies are split
            // into two identical halves - the second arrives exactly as the
            // first leaves.
            [0, 1].map((half) => (
              <div
                key={half}
                className="flex items-center gap-16 pr-16 sm:gap-24 sm:pr-24"
              >
                {Array.from({ length: copies }).map((_, c) =>
                  logos.map((logo) => (
                    <Logo
                      key={`${half}-${c}-${logo.id}`}
                      logo={logo}
                      // Only the very first pass is read out or reachable;
                      // everything after it is the same marks again.
                      hidden={half > 0 || c > 0}
                    />
                  ))
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function Logo({ logo, hidden }) {
  const img = (
    <img
      src={`${MEDIA}${logo.logo_path}`}
      alt={hidden ? "" : logo.name}
      loading="lazy"
      decoding="async"
      className="h-16 w-auto max-w-[260px] object-contain sm:h-24 sm:max-w-[340px]"
    />
  );

  if (hidden) return <span aria-hidden="true">{img}</span>;

  return logo.website ? (
    <a
      href={logo.website}
      target="_blank"
      rel="noreferrer"
      className="shrink-0 transition-opacity hover:opacity-70"
      title={logo.name}
    >
      {img}
    </a>
  ) : (
    <span className="shrink-0">{img}</span>
  );
}